import {
  AfterViewChecked,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { AnalyticsService } from '../../services/analytics.service';
import { EventsService } from '../../services/events.service';
import { EventCalendarService } from '../../services/event-calendar.service';
import { LanguageService } from '../../services/language.service';
import { EventDoc, EventLocation, EventMediaItem } from '../../models/event.model';
import { CityEventsService } from '../../services/city-events.service';
import {
  EventBookKind,
  EventIdentity,
  bookActionKind,
  directionsUrl,
  eventIdentity,
  formatEventDateRange,
  formatEventTimeRange,
  hasPromotion,
  heroMedia,
  locationLabel,
  primaryLocation,
} from '../event-utils';

/**
 * One event, full screen — iOS `EventDetailsView2`.
 *
 * A route rather than an overlay sheet (which is how attractions do it), for three reasons: the
 * home banner already navigates to `/event/:id` and has been dead-ending there, push notifications
 * deep-link by path, and "related events" needs to push another instance of this same screen.
 *
 * Reached with only an id, so it always fetches — there is no "pass the object in" path like iOS's,
 * which would not survive a reload or a shared link anyway.
 */
@Component({
    selector: 'app-event-details',
    templateUrl: './event-details.component.html',
    styleUrls: ['./event-details.component.scss'],
    host: { class: 'events-scope' },
    standalone: false
})
export class EventDetailsComponent implements OnInit, OnDestroy, AfterViewChecked {
  event: EventDoc | null = null;
  relatedEvents: EventDoc[] = [];

  loading = true;
  notFound = false;

  /**
   * Which source answered — the City segment's, or Firestore's.
   *
   * Only used to decide where "back" goes when there is no history to go back through (a shared
   * link, a push): the events screen must open on the segment this event belongs to, or a diner who
   * followed a city event's link lands on a venue programme that does not contain it.
   */
  private isCityEvent = false;

  descriptionExpanded = false;
  /** Set once the description is measured; drives whether the Read more toggle appears at all. */
  descriptionOverflows = false;

  toast: string | null = null;

  @ViewChild('description') descriptionRef?: ElementRef<HTMLElement>;

  private routeSub?: Subscription;
  private toastTimer: any = null;
  private measuredFor: string | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private location: Location,
    private eventsService: EventsService,
    private cityEventsService: CityEventsService,
    private calendar: EventCalendarService,
    private languageService: LanguageService,
    private translate: TranslateService,
    private analytics: AnalyticsService
  ) {}

  ngOnInit(): void {
    // paramMap, not a snapshot: navigating from "related events" lands on the SAME route with a new
    // id, and Angular reuses the component instead of recreating it. A snapshot read would leave
    // the previous event on screen forever.
    this.routeSub = this.route.paramMap.subscribe(params => {
      const id = params.get('id');
      if (!id) {
        this.router.navigate(['/events']);
        return;
      }
      this.load(id);
    });
  }

  ngOnDestroy(): void {
    this.routeSub?.unsubscribe();
    if (this.toastTimer) clearTimeout(this.toastTimer);
  }

  /**
   * Whether the description is actually clamped can only be known after layout, and it changes with
   * viewport width and language. Measured here rather than in `ngAfterViewInit` because the element
   * does not exist until the fetch resolves.
   *
   * Guarded by `measuredFor` so this runs once per event rather than on every change-detection
   * pass — reading `scrollHeight` forces a synchronous layout, which is exactly what you do not want
   * in a hook that fires constantly.
   */
  ngAfterViewChecked(): void {
    const element = this.descriptionRef?.nativeElement;
    const id = this.event?.id ?? null;
    if (!element || !id || this.measuredFor === id) return;

    this.measuredFor = id;
    const overflows = element.scrollHeight > element.clientHeight + 1;
    if (overflows !== this.descriptionOverflows) {
      // Deferred: this runs during change detection, and assigning synchronously would trip
      // ExpressionChangedAfterItHasBeenCheckedError in dev mode.
      setTimeout(() => (this.descriptionOverflows = overflows));
    }
  }

  private async load(eventId: string): Promise<void> {
    this.loading = true;
    this.notFound = false;
    this.event = null;
    this.relatedEvents = [];
    this.descriptionExpanded = false;
    this.descriptionOverflows = false;
    this.measuredFor = null;
    // Reset with the rest: "related events" reuses this component instance for a new id.
    this.isCityEvent = false;
    window.scrollTo(0, 0);

    const event = await this.resolveEvent(eventId);
    this.event = event;
    this.notFound = !event;
    this.loading = false;

    if (!event) return;

    // Fire-and-forget: the carousel appears when it resolves and the rest of the page does not wait.
    // Empty on every production document today, so this is normally a no-op.
    const relatedIds = (event.relatedFeedItems ?? [])
      .map(item => item?.id)
      .filter((id): id is string => !!id);
    if (relatedIds.length) {
      this.eventsService.getRelatedEvents(relatedIds).then(events => (this.relatedEvents = events));
    }
  }

  /**
   * The event behind an id, from EITHER source.
   *
   * This screen is reached with an id alone and always fetches — there is no "pass the object in"
   * path like iOS's, which is also why neither mobile app had to solve this. A venue event lives in
   * Firestore and a city event does not, so one lookup cannot answer for both, and a city event
   * opened from the segment (or from a shared link, or a push) would otherwise render "not found".
   *
   * Which source is tried FIRST comes off the navigation state the list sets, so the common in-app
   * path costs one request and not two. A cold deep link has no state: it tries Firestore, then the
   * city route. `getCityEvent` treats a `404` as "not a city event" and does not report it, so this
   * fallback cannot flood the error metric with ordinary misses.
   */
  private async resolveEvent(eventId: string): Promise<EventDoc | null> {
    const openedFromCity = (this.location.getState() as { source?: string } | null)?.source === 'city';

    if (openedFromCity) {
      const cityEvent = await this.cityEventsService.getCityEvent(eventId);
      if (cityEvent) {
        this.isCityEvent = true;
        return cityEvent;
      }
      return this.eventsService.getEvent(eventId);
    }

    const venueEvent = await this.eventsService.getEvent(eventId);
    if (venueEvent) return venueEvent;

    const cityEvent = await this.cityEventsService.getCityEvent(eventId);
    this.isCityEvent = !!cityEvent;
    return cityEvent;
  }

  // -------------------------------------------------------------------------
  // derived
  // -------------------------------------------------------------------------

  /**
   * Who the event is presented by: the venue, or — on a city event, which has none (**D-6**) — the
   * organiser, captioned. Null when there is neither, and the view drops the row rather than drawing
   * an empty avatar over a blank line (**I-21**).
   */
  get identity(): EventIdentity | null {
    return eventIdentity(this.event);
  }

  /**
   * The collapsed description, with blank lines removed.
   *
   * `white-space: pre-line` renders the authored newlines, which is right — but it also means a
   * paragraph break spends one of the clamp's lines, so a description that opens with a short line
   * ("Мостар Синдром, во живо") showed one line of text and a gap where the preview should be. The
   * blank lines are dropped here rather than by narrowing the clamp, because expanding must give
   * back the paragraphs exactly as authored. iOS hit and fixed the identical thing.
   */
  get descriptionPreview(): string {
    return (this.event?.description ?? '')
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0)
      .join('\n');
  }

  get media(): EventMediaItem | null {
    return heroMedia(this.event);
  }

  get hasPromotion(): boolean {
    return hasPromotion(this.event);
  }

  get date(): string {
    return formatEventDateRange(this.event, this.languageService.getCurrentLang());
  }

  get time(): string {
    return formatEventTimeRange(this.event);
  }

  /** Named `venueLocation`, not `location` — the injected Angular `Location` service owns that. */
  get venueLocation(): EventLocation | null {
    return primaryLocation(this.event);
  }

  get place(): string {
    return locationLabel(this.venueLocation);
  }

  get address(): string {
    return (this.venueLocation?.address ?? '').trim();
  }

  get directionsHref(): string | null {
    return directionsUrl(this.venueLocation);
  }

  get website(): string {
    return (this.event?.eventWebUrl ?? '').trim();
  }

  get phone(): string {
    return (this.event?.phoneNumber ?? '').trim();
  }

  get bookKind(): EventBookKind | null {
    return bookActionKind(this.event);
  }

  /** The admin's own button label when they set one, else the default. */
  get bookLabel(): string {
    const custom = (this.event?.bookAction?.title ?? '').trim();
    return custom || this.translate.instant('events.bookNow');
  }

  // -------------------------------------------------------------------------
  // actions
  // -------------------------------------------------------------------------

  onBook(): void {
    const kind = this.bookKind;
    const value = (this.event?.bookAction?.value ?? '').trim();
    if (!kind || !value) return;

    this.analytics.log('event_book_click', {
      content_id: this.event?.id ?? '',
      action_type: kind
    });

    if (kind === 'call') {
      window.location.href = `tel:${value}`;
    } else {
      window.open(value, '_blank', 'noopener');
    }
  }

  onAddToCalendar(): void {
    const saved = this.calendar.save(this.event);
    if (saved) {
      this.analytics.log('event_calendar_saved', { content_id: this.event?.id ?? '' });
    }
    this.showToast(
      this.translate.instant(saved ? 'events.calendarSaved' : 'events.calendarUnavailable')
    );
  }

  onOpenRelated(eventId: string): void {
    this.analytics.log('event_open', { content_id: eventId, entry_point: 'related' });
    this.router.navigate(['/event', eventId]);
  }

  toggleDescription(): void {
    this.descriptionExpanded = !this.descriptionExpanded;
  }

  back(): void {
    // A deep link (push, shared URL, banner on a fresh load) has nothing behind it in this app's
    // history, so `location.back()` would leave the site entirely. Fall back to the events list —
    // on the segment this event actually belongs to, or a city event's link would land the diner on
    // a venue programme that does not contain what they just read.
    if (window.history.length > 1) {
      this.location.back();
    } else if (this.isCityEvent) {
      this.router.navigate(['/events'], { queryParams: { segment: 'city' } });
    } else {
      this.router.navigate(['/events']);
    }
  }

  private showToast(message: string): void {
    this.toast = message;
    if (this.toastTimer) clearTimeout(this.toastTimer);
    this.toastTimer = setTimeout(() => (this.toast = null), 3000);
  }

  trackById(index: number, event: EventDoc): string {
    return event.id ?? String(index);
  }
}
